import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { businessUnitSchema } from "../business-units/schemas";

export interface GovernanceBusinessUnit {
  id: string;
  name: string;
  head: string;
  createdAt?: unknown;
  validationError?: string;
}

export type BusinessUnitsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: GovernanceBusinessUnit[] };

export function useBusinessUnits(): BusinessUnitsState & {
  reload: () => void;
} {
  const [state, setState] = useState<BusinessUnitsState>({
    status: "loading",
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    getDocs(query(collection(db, "business_units"), orderBy("name")))
      .then((snap) => {
        if (cancelled) return;
        const data: GovernanceBusinessUnit[] = [];
        snap.forEach((d) => {
          const stored = d.data();
          const result = businessUnitSchema.safeParse(stored);
          if (result.success) {
            data.push({
              ...result.data,
              head: result.data.head ?? "",
              id: d.id,
            });
            return;
          }
          data.push({
            id: d.id,
            name:
              typeof stored.name === "string" && stored.name
                ? stored.name
                : d.id,
            head: typeof stored.head === "string" ? stored.head : "",
            validationError: result.error.issues
              .map((issue) => issue.path.join("."))
              .filter(Boolean)
              .join(", "),
          });
        });
        setState({ status: "success", data });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          error:
            err instanceof Error
              ? err.message
              : "Failed to load business units",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const reload = useCallback(() => setTick((value) => value + 1), []);

  return { ...state, reload };
}
